import dayjs from 'dayjs';
import React, { useState } from 'react';
import { FiChevronLeft, FiChevronRight, FiSearch } from 'react-icons/fi';
import { CircleSpinner } from 'react-spinners-kit';
import { DashboardHeader } from '../../core/DashboardHeader';
import { DashboardWrapper } from '../../core/DashboardWrapper';
import { HorizontalContent } from '../../core/HorizontalContent';
import { RhButton, RhButtonMode } from '../../core/RhButton';
import { RhCard } from '../../core/RhCard';
import { RhConfirmModal } from '../../core/RhConfirmModal';
import { RhInput } from '../../core/RhInput';
import { RhModal } from '../../core/RhModal';
import { RhSelect, RhSelectOption } from '../../core/RhSelect';
import { RhTextArea } from '../../core/RhTextArea';
import { SizedBox } from '../../core/SizedBox';
import { colors, Description, H2, Regular } from '../../core/Styles';
import { UserRequestsProps } from '../containers/UserRequestsContainer';
import {
  ActionButton,
  ActionButtonsContainer,
  Ellipsis,
  EmptyStateDiv,
  List,
  ListBody,
  ListHeader,
  ListHeaderItem,
  ListItem,
  ListItemColumn,
  ListItemHorizontalDivider,
  PageInfo,
  PageNumberButton,
  PageNumbers,
  PaginationButton,
  PaginationContainer,
  RequestTypeBadge,
  RequestTypeFilterWrapper,
  StatusBadge,
  StatusFilterWrapper,
} from './UserRequestsScreen.styles';

export const UserRequestsScreen: React.FC<UserRequestsProps> = props => {
  const [denyReason, setDenyReason] = useState<string>('');

  const formatRequestType = (type: string) => {
    return type
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const formatDate = (date: Date | undefined) => {
    if (!date) return '-';
    return dayjs(date).format('MMM DD, YYYY');
  };

  const handleDenyModalClose = () => {
    setDenyReason('');
    props.userClosedDenyModal();
  };

  const handleDenyConfirm = () => {
    if (props.denyRequestId) {
      props.userConfirmedDeny(props.denyRequestId, denyReason);
      setDenyReason('');
    }
  };

  const getRequestEmail = (requestId: string | null) => {
    if (!requestId) return '';
    const request = props.requests.find(r => r.id === requestId);
    return request?.userEmail || '';
  };

  const renderPagination = () => {
    if (props.totalPages <= 0) return null;

    const pages: (number | 'ellipsis')[] = [];
    const maxVisiblePages = 5;

    if (props.totalPages <= maxVisiblePages) {
      // Show all pages if total pages is less than max
      for (let i = 1; i <= props.totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      let startPage = Math.max(2, props.currentPage - 1);
      let endPage = Math.min(props.totalPages - 1, props.currentPage + 1);

      // Adjust if we're near the start
      if (props.currentPage <= 3) {
        endPage = 4;
      }

      // Adjust if we're near the end
      if (props.currentPage >= props.totalPages - 2) {
        startPage = props.totalPages - 3;
      }

      // Add ellipsis after first page if needed
      if (startPage > 2) {
        pages.push('ellipsis');
      }

      // Add middle pages
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }

      // Add ellipsis before last page if needed
      if (endPage < props.totalPages - 1) {
        pages.push('ellipsis');
      }

      // Always show last page
      pages.push(props.totalPages);
    }

    return (
      <PaginationContainer>
        <PaginationButton
          disabled={props.currentPage === 1}
          onClick={() => props.userChangedPage(props.currentPage - 1)}>
          <FiChevronLeft size={20} />
        </PaginationButton>

        <PageNumbers>
          {pages.map((page, index) => {
            if (page === 'ellipsis') {
              return <Ellipsis key={`ellipsis-${index}`}>...</Ellipsis>;
            }
            return (
              <PageNumberButton
                key={page}
                active={page === props.currentPage}
                onClick={() => props.userChangedPage(page)}>
                {page}
              </PageNumberButton>
            );
          })}
        </PageNumbers>

        <PaginationButton
          disabled={props.currentPage === props.totalPages}
          onClick={() => props.userChangedPage(props.currentPage + 1)}>
          <FiChevronRight size={20} />
        </PaginationButton>

        <PageInfo>
          Page {props.currentPage} of {props.totalPages}
        </PageInfo>
      </PaginationContainer>
    );
  };

  return (
    <DashboardWrapper>
      <DashboardHeader title="User Requests" />
      <RhCard>
        <HorizontalContent
          right={[
            props.isLoadingRequests && <CircleSpinner key="spinner" size={15} color={colors.primary} />,
            <StatusFilterWrapper key="status-filter">
              <RhSelect
                value={{
                  value: props.statusFilter,
                  label:
                    props.statusFilter === 'all'
                      ? 'All'
                      : props.statusFilter === 'deleted'
                      ? 'Deleted'
                      : props.statusFilter.charAt(0).toUpperCase() + props.statusFilter.slice(1),
                }}
                onChange={(option: RhSelectOption) => {
                  props.userUpdatedStatusFilter(option.value);
                }}
                options={[
                  { value: 'all', label: 'All' },
                  { value: 'pending', label: 'Pending' },
                  { value: 'accepted', label: 'Accepted' },
                  { value: 'denied', label: 'Denied' },
                  { value: 'deleted', label: 'Deleted' },
                ]}
                placeholder="Status"
              />
            </StatusFilterWrapper>,
            <RequestTypeFilterWrapper key="type-filter">
              <RhSelect
                value={{
                  value: props.requestTypeFilter,
                  label: props.requestTypeFilter === 'all' ? 'All' : formatRequestType(props.requestTypeFilter),
                }}
                onChange={(option: RhSelectOption) => {
                  props.userUpdatedRequestTypeFilter(option.value);
                }}
                options={[
                  { value: 'all', label: 'All' },
                  { value: 'account_deletion', label: 'Account Deletion' },
                  { value: 'role_change', label: 'Role Change' },
                ]}
                placeholder="Request Type"
              />
            </RequestTypeFilterWrapper>,
            <RhInput
              key="search-box"
              onChange={e => {
                props.userUpdatedSearchTerm(e.target.value);
              }}
              value={props.searchTerm}
              icon={FiSearch}
              placeholder="Search by email"
            />,
          ]}
        />

        <List>
          <ListHeader>
            <ListHeaderItem span={props.isSystemAdmin ? 12 : 15}>Email</ListHeaderItem>
            {props.isSystemAdmin && <ListHeaderItem span={10}>Church</ListHeaderItem>}
            <ListHeaderItem span={10}>Role</ListHeaderItem>
            <ListHeaderItem span={12}>Request Type</ListHeaderItem>
            <ListHeaderItem span={10}>Requested Role</ListHeaderItem>
            <ListHeaderItem span={10}>Status</ListHeaderItem>
            <ListHeaderItem span={10}>Date</ListHeaderItem>
            <ListHeaderItem span={props.isSystemAdmin ? 12 : 15}>Actions</ListHeaderItem>
          </ListHeader>
          <ListBody>
            {props.paginatedRequests.length === 0 ? (
              <EmptyStateDiv>
                <Regular>No requests found</Regular>
              </EmptyStateDiv>
            ) : (
              props.paginatedRequests.map((request, index) => (
                <div key={request.id}>
                  <ListItem>
                    <ListItemColumn span={props.isSystemAdmin ? 12 : 15}>
                      <span>{request.userEmail || '-'}</span>
                    </ListItemColumn>
                    {props.isSystemAdmin && (
                      <ListItemColumn span={10}>
                        <span>{request.churchName || '-'}</span>
                      </ListItemColumn>
                    )}
                    <ListItemColumn span={10}>
                      <span>{request.userRole || '-'}</span>
                    </ListItemColumn>
                    <ListItemColumn span={12}>
                      <RequestTypeBadge>{formatRequestType(request.requestType)}</RequestTypeBadge>
                    </ListItemColumn>
                    <ListItemColumn span={10}>
                      <span>{request.requestType === 'role_change' ? request.requestedRole || '-' : '-'}</span>
                    </ListItemColumn>
                    <ListItemColumn span={10}>
                      <StatusBadge status={request.status}>{request.status}</StatusBadge>
                    </ListItemColumn>
                    <ListItemColumn span={10}>
                      <span>{formatDate(request.createdAt)}</span>
                    </ListItemColumn>
                    <ListItemColumn span={props.isSystemAdmin ? 12 : 15}>
                      <ActionButtonsContainer>
                        {request.status === 'pending' ? (
                          request.requestType === 'account_deletion' ? (
                            <ActionButton
                              variant="deny"
                              onClick={() => props.userClickedDelete(request.id)}
                              disabled={props.isLoadingRequests || props.isProcessingRequest}>
                              Delete
                            </ActionButton>
                          ) : (
                            <>
                              <ActionButton
                                variant="accept"
                                onClick={() => props.userAcceptedRequest(request.id)}
                                disabled={props.isLoadingRequests || props.isProcessingRequest}>
                                Accept
                              </ActionButton>
                              <ActionButton
                                variant="deny"
                                onClick={() => props.userClickedDeny(request.id)}
                                disabled={props.isLoadingRequests || props.isProcessingRequest}>
                                Deny
                              </ActionButton>
                            </>
                          )
                        ) : (
                          <Regular color={colors.grey} style={{ fontSize: '12px' }}>
                            {request.status === 'accepted'
                              ? 'Accepted'
                              : request.status === 'deleted'
                              ? 'Deleted'
                              : request.status === 'denied'
                              ? 'Denied'
                              : request.status}{' '}
                            on {formatDate(request.actionTakenAt)}
                          </Regular>
                        )}
                      </ActionButtonsContainer>
                    </ListItemColumn>
                  </ListItem>
                  {index < props.paginatedRequests.length - 1 && <ListItemHorizontalDivider />}
                </div>
              ))
            )}
          </ListBody>
        </List>

        {renderPagination()}
      </RhCard>

      {/* Delete Confirmation Modal */}
      <RhConfirmModal
        isOpen={props.isDeleteModalOpen}
        description={`Are you sure you want to delete the account for ${getRequestEmail(
          props.deleteRequestId
        )}? This action cannot be undone.`}
        onClose={props.userClosedDeleteModal}
        onOk={() => {
          if (props.deleteRequestId) {
            props.userConfirmedDelete(props.deleteRequestId);
          }
        }}
        isOkLoading={props.isProcessingRequest}
        isCloseDisabled={props.isProcessingRequest}
        isOkDisabled={props.isProcessingRequest}
      />

      {/* Deny Confirmation Modal with Reason */}
      <RhModal
        isOpen={props.isDenyModalOpen}
        onRequestClose={handleDenyModalClose}
        contentStyle={styles.denyModalContent}>
        <Description>Are you sure you want to deny the request for {getRequestEmail(props.denyRequestId)}?</Description>
        <SizedBox height={16} />
        <Regular style={{ marginBottom: 8, fontWeight: 600 }}>Reason for denial (required):</Regular>
        <div style={styles.denyReasonTextArea}>
          <RhTextArea
            value={denyReason}
            onChange={e => setDenyReason(e.target.value)}
            placeholder="Enter reason for denial..."
            style={{ minHeight: '100px' }}
          />
        </div>
        <SizedBox height={20} />
        <HorizontalContent
          style={styles.denyModalFooter}
          right={[
            <RhButton
              key="deny-cancel"
              mode={RhButtonMode.text}
              title="Cancel"
              textStyle={styles.denyModalCancel}
              onClick={handleDenyModalClose}
              disabled={props.isLoadingRequests || props.isProcessingRequest}
            />,
            <RhButton
              key="deny-confirm"
              mode={RhButtonMode.text}
              title="Confirm Deny"
              textStyle={styles.denyModalConfirm}
              onClick={handleDenyConfirm}
              disabled={props.isLoadingRequests || props.isProcessingRequest || !denyReason.trim()}
              loading={props.isProcessingRequest}
            />,
          ]}
        />
      </RhModal>
    </DashboardWrapper>
  );
};

const styles = {
  denyModalContent: {
    minWidth: '500px',
  },
  denyReasonTextArea: {
    width: '100%',
  },
  denyModalFooter: {
    justifyContent: 'flex-end',
  },
  denyModalConfirm: {
    fontWeight: 900,
    fontSize: 16,
  },
  denyModalCancel: {
    fontWeight: 600,
    fontSize: 16,
  },
};
